import _, { Cancelable } from 'lodash'
import React from 'react'
import { IKioskComponentProps } from './checkin'
import './_appointment-search.scss'
import { AppointmentCard } from './appointment-card'
import { KioskInputField } from '../utils'
import { Entity } from 'shared-libs/models/entity'
import { LoadingSpinner } from 'browser/components/atomic-elements/atoms/loading-spinner/loading-spinner'
import classNames from 'classnames'
import apis from 'browser/app/models/apis'
import { CustomFormulas } from 'shared-libs/helpers/formulas'
import { EsprimaParser, ExpressionEvaluator, ValueGetter } from 'shared-libs/helpers/evaluation'
import { materializeProps } from 'shared-libs/models/types/storyboard/storyboard-utils'
import { Query } from 'shared-libs/models/query'
import { Collection } from 'shared-libs/models/collection'
import { edgeFromEntity } from 'shared-libs/helpers/formulas/modules'
import { DEBOUNCE_TIMEOUT } from 'browser/app/utils/utils'
import { FramesManager } from 'shared-libs/components/view/frames-manager'

interface IAppointmentLookupState {
  queryTerm: string
  queryResults?: Entity[]
  selectedItem: Entity | null
  searchState: 'idle' | 'loading' | 'success' | 'not-found'
}

interface IApppointmentSearchProps extends IKioskComponentProps {
  minQueryLength?: number
  maxQueryResults?: number
  autoSelect?: boolean // auto select there is only result
  helpText?: string | object //  either string or actual ui schema
  searchQuery?: any // ES query
  onNotFound?: () => void
  frames?: FramesManager
}

export class AppointmentSearch extends React.Component<
  IApppointmentSearchProps,
  IAppointmentLookupState
> {
  private collection: any
  private evaluator: ExpressionEvaluator
  private debouncedSearch: (() => Promise<Entity[]>) & Cancelable

  public static defaultProps: Partial<IApppointmentSearchProps> = {
    minQueryLength: 3,
    maxQueryResults: 5,
    autoSelect: true,
  }

  constructor(props: IKioskComponentProps) {
    super(props)
    const { value } = props
    this.state = {
      queryTerm: '',
      queryResults: [],
      selectedItem: value,
      searchState: 'idle',
    }

    this.evaluator = this.createQueryEvaluator()
    this.debouncedSearch = _.debounce(this.performSearch, DEBOUNCE_TIMEOUT)
  }

  public componentWillUnmount(): void {
    this.debouncedSearch.cancel()
    this.collection?.dispose()
  }

  public render() {
    const { queryTerm } = this.state

    return (
      <div className="kiosk-appointment-search">
        {this.renderHelpText()}
        <KioskInputField
          placeholder="Search for your appointment"
          value={queryTerm}
          iconName="search"
          iconSize="2rem"
          onChange={this.onSearchQueryChange}
        />

        {this.renderResult()}
      </div>
    )
  }

  private renderHelpText = () => {
    const { helpText } = this.props
    if (!helpText) {
      return (
        <p className="kiosk-appointment-search__instructions">
          PO Numbers start with <strong>RS</strong> followed by eight digits; i.e.,{' '}
          <code>RS41601234</code>
        </p>
      )
    }

    if (typeof helpText === 'string') {
      return <p className="kiosk-appointment-search__instructions">{helpText}</p>
    }

    return this.renderHelpTextSchema(helpText)
  }

  private renderHelpTextSchema = (uiSchema) => {
    const { frames } = this.props
    const renderer = frames?.getContext('renderer')
    const newFrames = renderer.createChildFrame(frames, { uiSchema, uiSchemaPath: [] })
    return renderer.createElementFromFrame(newFrames, { ...this.state })
  }

  private onSearchQueryChange = (value) => {
    const queryTerm = value.trim()

    this.setState({ queryTerm })

    if (!this.isValidSearchValue(queryTerm)) {
      this.collection?.dispose()
      this.collection?.clearContent()
      this.setState({ queryResults: [], searchState: 'idle', selectedItem: undefined })
      return
    }

    this.debouncedSearch()
  }

  private handleSelectItem = (value: Entity | null) => {
    const { onChange } = this.props
    onChange?.(edgeFromEntity(value))
    this.setState({
      selectedItem: value,
    })
  }

  // render helpers
  private renderResult = (): JSX.Element => {
    switch (this.state.searchState) {
      case 'loading': {
        return this.renderLoading()
      }
      case 'success': {
        return this.renderSuccess(this.state.queryResults)
      }
      case 'not-found': {
        return this.renderNotFound()
      }
    }
    return <div />
  }

  private renderLoading = (): JSX.Element => {
    return <LoadingSpinner />
  }

  private renderSuccess = (queryResults: Entity[]): JSX.Element => {
    return (
      <div className="kiosk-appointment-search-results">
        <p className="kiosk-appointment-search-results__label">
          Matching Appointments (select one):
        </p>
        {queryResults.map((entity) => this.renderItem(entity))}
      </div>
    )
  }

  private renderNotFound = (): JSX.Element => {
    return (
      <p className="kiosk-appointment-search-not-found">
        No matching appointment found. Update your appointment number or continue checking in
        without an appointment.
      </p>
    )
  }

  private renderItem = (item: Entity) => {
    return (
      <div
        key={item.uniqueId}
        className={classNames('kiosk-appointment-search-results__item', {
          ['selected']: this.state.selectedItem?.uniqueId === item.uniqueId,
        })}
        onClick={() => this.handleSelectItem(item)}
      >
        <AppointmentCard value={item} />
      </div>
    )
  }

  // search
  private performSearch = async (): Promise<Entity[]> => {
    this.collection?.dispose()

    if (!this.isValidSearchValue(this.state.queryTerm)) {
      return []
    }

    const searchQuery = this.buildQuery()

    const query = new Query(apis)
      .setEntityType(searchQuery?.entityType)
      .setOrders(searchQuery?.orders)
      .setFilters(searchQuery?.filters)
      .setMetadata(searchQuery?.metadata)

    this.collection = new Collection(apis, query)

    this.setState({ searchState: 'loading' })
    const entities = await this.collection.find()

    this.setState({
      queryResults: entities,
      searchState: _.isEmpty(entities) ? 'not-found' : 'success',
    })

    if (this.props.autoSelect && entities.length === 1) {
      this.handleSelectItem(entities[0])
    } else {
      this.handleSelectItem(null)
    }

    if (entities.length === 0) {
      this.props.onNotFound?.()
    }

    return entities
  }

  private isValidSearchValue = (value: string): boolean => {
    return value && value.trim().length >= this.props.minQueryLength
  }

  private createQueryEvaluator = () => {
    const context = {
      settings: apis.getSettings(),
      ...CustomFormulas,
    }

    const valueGetter: ValueGetter = (key) => {
      return _.get(this.state, key) ?? _.get(this.props, key) ?? _.get(context, key)
    }

    return ExpressionEvaluator.create().setASTParser(EsprimaParser).setValueGetter(valueGetter)
  }

  private buildQuery = () => {
    // TODO: need to get kiosk query from facility
    const expandedValues = { type: 'expression', value: 'GENERATE_SEARCH_VARIANTS(queryTerm, 4)' }
    const defaultQuery = {
      filters: [
        {
          type: 'or',
          orFilters: [
            [
              {
                type: 'prefix',
                path: 'core_calendar_appointment.name',
                values: expandedValues,
              },
            ],
            [
              {
                type: 'prefix',
                path: 'core_yms_carrierAppointment.shipmentNumber',
                values: expandedValues,
              },
            ],
            [
              {
                type: 'prefix',
                path: 'core_yms_carrierAppointment.loadNumber',
                values: expandedValues,
              },
            ],
            [
              {
                type: 'contains',
                path: 'core_yms_carrierAppointment.deliveryNumbers',
                values: expandedValues,
              },
            ],
            [
              {
                type: 'contains',
                path: 'core_yms_carrierAppointment.purchaseOrderNumbers',
                values: expandedValues,
              },
            ],
          ],
        },
        {
          type: 'match',
          path: 'core_calendar_appointment.status',
          values: ['Open', 'Missed', 'Active'],
        },
        {
          type: 'matchEdge',
          path: 'core_yms_carrierAppointment.facility',
          values: {
            type: 'expression',
            value:
              "(MAP(settings.getUser()?.user?.facilityList, 'facility').concat(settings.getUser()?.user?.facility ? [settings.getUser()?.user?.facility] : []).length > 0 ? MAP(settings.getUser()?.user?.facilityList, 'facility').concat(settings.getUser()?.user?.facility ? [settings.getUser()?.user?.facility] : []) : undefined)",
          },
        },
      ],
      entityType: '/1.0/entities/metadata/core_yms_carrierAppointment.json',
      entityDisplayName: 'Appointment',
      metadata: {
        offset: 0,
        shouldIncludeLeafEntities: true,
        size: {
          type: 'expression',
          value: 'maxQueryResults',
        },
      },
    }

    // query evaluator has access to the following
    // - settings: Settings object
    // - queryTerm: current search value
    // - selectedItem: current selected item
    const query = this.props.searchQuery ?? defaultQuery
    return materializeProps(query, this.evaluator)
  }
}
